import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { useGmSession } from '../useGmSession';
import RoadFighterScene, {
    FuelMeter,
    TrackProgressMeter,
    activeLanes,
    laneValidAt,
    roadProfileForLevel,
    validLanesForCount,
} from '../visuals/RoadFighterScene';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createRoadFighterSession,
    deleteRoadFighterSession,
    moveRoadFighterSession,
    gmToRoadView,
} from './roadFighterGmClient';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'road_fighter';
const { UPLOAD_URL } = ARCADE.road_fighter;
const ACCENT = GAME_ACCENT.road_fighter;
const LANES = 3;
const MAX_LEVEL = 5;
const SPEED_DIST_SCALE = 0.08;
const FUEL_PICKUP_RANGE = 12;
const HEALTH_PICKUP_RANGE = 12;
export const COLLISION_RANGE = 10;
const PACK_CLEAR = 36;
/** Below this km/h the player is treated as stopped — no crash or edge-skid damage. */
export const STOPPED_EPS = 1;
/** Must match game_engines/road_fighter/road_fighter_gm.py — scales meters into ~0–1000. */
export const DISTANCE_SCORE_DIVISOR = 17;
const HINT = '← → / A D change lane · ↑ / W accel · ↓ / S brake · Esc pause · Enter / Space continue after a stage';
const TICK_MS = 180;

export const distanceToScore = (campaignDistance) => (
    Math.floor(Number(campaignDistance || 0) / DISTANCE_SCORE_DIVISOR)
);

/** Cap on how much damage a clear bonus can repair so later stages stay challenging. */
export const CLEAR_REPAIR_MAX = 30;

/** Pickup packs fully restore health (`player.damage = 0`); leftover in LEVELS for config parity. */
export const HEALTH_PACK_VALUE = 32;

/** Same-lane spacing and “row” width so traffic never forms a 3-wide wall. */
export const MIN_OBSTACLE_GAP = 52;
export const DENSITY_HORIZON = 80;
export const CLUSTER_SLACK = 22;
export const MAX_NEARBY_OBSTACLES = { 1: 2, 2: 2, 3: 2, 4: 2, 5: 1 };
/** Sprite length (m) along the road — trucks occupy a longer slot than bikes. */
export const KIND_LENGTH = { bike: 8, car: 14, truck: 22 };
export const LANE_CLEARANCE_MIN = 18;

/** Fraction of L3+ spawns that weave. Bikes bias up, trucks down. */
export const WEAVE_CHANCE = { 1: 0, 2: 0, 3: 0.37, 4: 0.42, 5: 0.52 };
export const WEAVE_PERIOD_MIN = 6;
export const WEAVE_PERIOD_MAX = 12;

export const LEVELS = {
    1: { track_length: 1600, max_speed: 360, min_speed: 0, accel_step: 28, brake_step: 22, obstacle_rate: 0.04, fuel_drain: 0.42, fuel_pack_value: 32, fuel_pack_count: 3, health_pack_value: HEALTH_PACK_VALUE, health_pack_count: 2, start_speed: 140 },
    2: { track_length: 2400, max_speed: 400, min_speed: 0, accel_step: 26, brake_step: 24, obstacle_rate: 0.052, fuel_drain: 0.50, fuel_pack_value: 30, fuel_pack_count: 4, health_pack_value: HEALTH_PACK_VALUE, health_pack_count: 2, start_speed: 150 },
    3: { track_length: 3200, max_speed: 450, min_speed: 0, accel_step: 24, brake_step: 26, obstacle_rate: 0.068, fuel_drain: 0.58, fuel_pack_value: 28, fuel_pack_count: 5, health_pack_value: HEALTH_PACK_VALUE, health_pack_count: 3, start_speed: 160 },
    4: { track_length: 4200, max_speed: 500, min_speed: 0, accel_step: 22, brake_step: 28, obstacle_rate: 0.08, fuel_drain: 0.65, fuel_pack_value: 26, fuel_pack_count: 6, health_pack_value: HEALTH_PACK_VALUE, health_pack_count: 3, start_speed: 170 },
    5: { track_length: 5400, max_speed: 560, min_speed: 0, accel_step: 20, brake_step: 30, obstacle_rate: 0.047, fuel_drain: 0.72, fuel_pack_value: 24, fuel_pack_count: 7, health_pack_value: HEALTH_PACK_VALUE, health_pack_count: 4, start_speed: 180 },
};

const levelCfg = (level) => LEVELS[Math.max(1, Math.min(MAX_LEVEL, Number(level) || 1))];

/** Oncoming traffic kinds — keep in sync with road_fighter_gm.py. */
export const VEHICLE_KINDS = ['bike', 'car', 'truck'];

/** Crash damage vs a car at the same player speed. Bike scrape < car < truck. */
export const KIND_DAMAGE = { bike: 0.52, car: 1, truck: 1.6 };

/** Closing speed (km/h) subtracted from obstacle distance each tick. Bikes arrive sooner. */
export const KIND_REL_SPEED = { bike: 78, car: 42, truck: 18 };

/** Spawn weights by stage. Trucks are zeroed in 1- and 2-lane pinches. */
export const KIND_WEIGHTS = {
    1: { car: 0.90, bike: 0.10, truck: 0 },
    2: { car: 0.55, bike: 0.42, truck: 0.03 },
    3: { car: 0.58, bike: 0, truck: 0.42 },
    4: { car: 0.40, bike: 0.28, truck: 0.32 },
    5: { car: 0.22, bike: 0.35, truck: 0.43 },
};

export const vehicleKindFactor = (kind) => (
    KIND_DAMAGE[kind] != null ? KIND_DAMAGE[kind] : 1
);

export const vehicleLength = (kind) => (
    KIND_LENGTH[kind] != null ? KIND_LENGTH[kind] : KIND_LENGTH.car
);

/** Same-lane gap so two sprites plus the collision box never occupy one slot. */
export const laneClearance = (kind, otherKind = 'car') => {
    const span = (vehicleLength(kind) + vehicleLength(otherKind)) / 2;
    return Math.max(LANE_CLEARANCE_MIN, COLLISION_RANGE + span);
};

export const isPlayerStopped = (speed) => Number(speed) < STOPPED_EPS;

export const pickVehicleKind = (level, laneCount, rng = Math.random) => {
    const lv = Math.max(1, Math.min(MAX_LEVEL, Math.round(Number(level) || 1)));
    const weights = { ...KIND_WEIGHTS[lv] };
    if (Number(laneCount) < 3) weights.truck = 0;
    const total = VEHICLE_KINDS.reduce((s, k) => s + (weights[k] || 0), 0);
    if (total <= 0) return 'car';
    let r = rng() * total;
    for (const kind of VEHICLE_KINDS) {
        r -= weights[kind] || 0;
        if (r <= 0) return kind;
    }
    return 'car';
};

export const maxNearbyFor = (level) => {
    const lv = Math.max(1, Math.min(MAX_LEVEL, Math.round(Number(level) || 1)));
    return MAX_NEARBY_OBSTACLES[lv] || 2;
};

export const nearbyObstacleCount = (obstacles, playerDistance, horizon = DENSITY_HORIZON) => (
    (obstacles || []).filter((o) => {
        const gap = Number(o.distance) - Number(playerDistance);
        return gap > 0 && gap < horizon;
    }).length
);

export const lanesOccupiedNear = (obstacles, distance, slack = CLUSTER_SLACK) => {
    const occupied = new Set();
    for (const o of obstacles || []) {
        if (Math.abs(Number(o.distance) - Number(distance)) <= slack) {
            occupied.add(Number(o.lane));
        }
    }
    return occupied;
};

export const hasSafeLane = (obstacles, distance, laneCount, slack = CLUSTER_SLACK) => {
    const valid = validLanesForCount(laneCount);
    const occupied = lanesOccupiedNear(obstacles, distance, slack);
    return valid.some((l) => !occupied.has(l));
};

export const laneHasGap = (obstacles, lane, distance, minGap = MIN_OBSTACLE_GAP) => (
    !(obstacles || []).some((o) => (
        Number(o.lane) === Number(lane)
        && Math.abs(Number(o.distance) - Number(distance)) < minGap
    ))
);

/** True when `lane` at `distance` is clear of other obstacles for this vehicle kind. */
export const laneFree = (obstacles, lane, distance, kind = 'car') => (
    !(obstacles || []).some((o) => (
        Number(o.lane) === Number(lane)
        && Math.abs(Number(o.distance) - Number(distance)) < laneClearance(kind, o.kind)
    ))
);

/** Pick a lane that keeps a gap and leaves at least one free lane in this cluster. */
export const pickSafeSpawnLane = (obstacles, distance, lanes, rng = Math.random, kind = 'car') => {
    const valid = lanes && lanes.length ? lanes : [1];
    const occupied = lanesOccupiedNear(obstacles, distance);
    const candidates = valid.filter((l) => (
        !occupied.has(l)
        && laneHasGap(obstacles, l, distance)
        && laneFree(obstacles, l, distance, kind)
    ));
    if (valid.length <= 1) return candidates[0] ?? null;
    if (valid.filter((l) => !occupied.has(l)).length <= 1) return null;
    if (!candidates.length) return null;
    const idx = Math.floor(rng() * candidates.length) % candidates.length;
    return candidates[idx];
};

export const shouldWeave = (level, kind, rng = Math.random) => {
    const lv = Math.max(1, Math.min(MAX_LEVEL, Math.round(Number(level) || 1)));
    if (lv < 3) return false;
    let p = WEAVE_CHANCE[lv] || 0;
    if (kind === 'bike') p = Math.min(0.85, p + 0.15);
    else if (kind === 'truck') p *= 0.55;
    return rng() < p;
};

export const stepWeavers = (obstacles, level, trackLength, rng = Math.random) => {
    const lv = Number(level) || 1;
    const list = obstacles || [];
    const out = [];
    for (let i = 0; i < list.length; i += 1) {
        const obs = list[i];
        const others = [...out, ...list.slice(i + 1)];
        const laneCount = activeLanes(obs.distance, level, trackLength);
        const lanes = [...validLanesForCount(laneCount)];
        const kind = obs.kind;
        let lane = lanes.includes(obs.lane) ? obs.lane : null;
        if (lane == null) {
            const free = lanes.filter((l) => laneFree(others, l, obs.distance, kind));
            const pool = free.length ? free : lanes;
            lane = pool[Math.floor(pool.length / 2)] ?? 1;
        }
        const next = { ...obs, lane };
        if (!obs.weave || lv < 3) {
            out.push(next);
            continue;
        }
        if (obs.kind === 'truck' && laneCount < 3) {
            next.weave = false;
            out.push(next);
            continue;
        }
        const cooldown = Math.max(0, Number(obs.weaveCooldown) || 0);
        if (cooldown > 0) {
            next.weaveCooldown = cooldown - 1;
            out.push(next);
            continue;
        }
        const occupied = lanesOccupiedNear(others, obs.distance);
        const options = lanes.filter((l) => (
            l !== lane && !occupied.has(l) && laneFree(others, l, obs.distance, kind)
        ));
        if (!options.length) {
            // Stay put this tick rather than overlapping an occupied slot.
            next.weaveCooldown = 0;
            out.push(next);
            continue;
        }
        const idx = Math.floor(rng() * options.length) % options.length;
        const target = options[idx];
        next.lane = target;
        next.targetLane = target;
        next.weaveCooldown = Number(obs.weavePeriod) || WEAVE_PERIOD_MIN;
        out.push(next);
    }
    return out;
};

/** If L1's view is empty, still spawn so the first stage always shows traffic. */
export const L1_TRAFFIC_HORIZON = 160;

/**
 * Random roll, plus a guaranteed L1 spawn when nothing is on-screen.
 * Extra `rng() < 1` lets tests pass a stub that always returns 1 (no spawn).
 * Rate 0 disables the guarantee so existing tests can clear the highway.
 * Nearby-count cap keeps the next 80 m from filling wall-to-wall.
 */
export const wantsObstacleSpawn = (level, cfg, playerDistance, obstacles, rng, trackLength) => {
    if (nearbyObstacleCount(obstacles, playerDistance) >= maxNearbyFor(level)) return false;
    if (rng() < cfg.obstacle_rate) return true;
    if (Number(level) !== 1 || !(cfg.obstacle_rate > 0)) return false;
    if (playerDistance > (Number(trackLength) || 0) - 140) return false;
    const hasAhead = (obstacles || []).some((o) => {
        const gap = Number(o.distance) - playerDistance;
        return gap > 0 && gap < L1_TRAFFIC_HORIZON;
    });
    if (hasAhead) return false;
    return rng() < 1;
};

export const spawnObstacle = (playerDistance, level, rng = Math.random, existing = []) => {
    const cfg = levelCfg(level);
    const lv = Math.max(1, Math.min(MAX_LEVEL, Math.round(Number(level) || 1)));
    for (let attempt = 0; attempt < 4; attempt += 1) {
        const dist = playerDistance + 80 + rng() * 100 + attempt * MIN_OBSTACLE_GAP;
        const laneCount = activeLanes(dist, level, cfg.track_length);
        const lanes = validLanesForCount(laneCount);
        const kind = pickVehicleKind(level, laneCount, rng);
        const lane = pickSafeSpawnLane(existing, dist, lanes, rng, kind);
        if (lane == null) continue;
        const weave = shouldWeave(lv, kind, rng);
        const period = WEAVE_PERIOD_MIN
            + Math.floor(rng() * (WEAVE_PERIOD_MAX - WEAVE_PERIOD_MIN + 1));
        return {
            lane,
            distance: dist,
            kind,
            speed: KIND_REL_SPEED[kind],
            weave,
            weavePeriod: period,
            weaveCooldown: period,
            targetLane: lane,
        };
    }
    return null;
};

const obstacleRelSpeed = (obs) => {
    const n = Number(obs?.speed);
    if (Number.isFinite(n) && n > 0) return n;
    if (obs?.kind && KIND_REL_SPEED[obs.kind] != null) return KIND_REL_SPEED[obs.kind];
    return 0;
};

const vehicleKindOf = (obs) => (
    VEHICLE_KINDS.includes(obs?.kind) ? obs.kind : 'car'
);

export const speedDamage = (speed, maxSpeed, kind = 'crash') => {
    if (isPlayerStopped(speed)) return 0;
    const vmax = Math.max(1, Number(maxSpeed) || 1);
    const ratio = Math.max(0, Math.min(1, Number(speed) / vmax));
    if (kind === 'edge') {
        return Math.max(4, Math.round(6 + ratio * 28));
    }
    const base = Math.max(8, Math.round(12 + ratio * 48));
    const factor = vehicleKindFactor(kind);
    return Math.max(1, Math.round(base * factor));
};

export const applySpeedImpact = (player, cfg, kind) => {
    if (isPlayerStopped(player.speed)) return 0;
    const dmg = speedDamage(player.speed, cfg.max_speed, kind);
    player.damage = Math.min(100, player.damage + dmg);
    const factor = kind === 'edge' ? 0.7 : 0.55;
    player.speed = Math.max(0, player.speed * factor);
    return dmg;
};

/**
 * Level-clear bonus from remaining fuel, damage, and leftover speed.
 * Fuel refill 20–40 (more if the tank is empty or the finish was clean), capped at 100.
 * Repair is higher on low/medium damage, at most CLEAR_REPAIR_MAX, never below 0.
 * Bonus meters feed campaignDistance / the leaderboard.
 */
export const computeLevelClearReward = (player, cfg) => {
    const fuel = Math.max(0, Math.min(100, Number(player?.fuel) || 0));
    const damage = Math.max(0, Math.min(100, Number(player?.damage) || 0));
    const speed = Math.max(0, Number(player?.speed) || 0);
    const maxSpeed = Math.max(1, Number(cfg?.max_speed) || 1);

    const emptiness = 1 - fuel / 100;
    const fuelClean = fuel / 100;
    const intendedFuel = Math.round(20 + Math.max(emptiness, fuelClean) * 20);
    const fuelGain = Math.round(Math.min(100 - fuel, intendedFuel));
    const fuelAfter = Math.min(100, fuel + fuelGain);

    const dmgClean = 1 - damage / 100;
    const intendedRepair = Math.min(CLEAR_REPAIR_MAX, Math.round(10 + dmgClean * 20));
    const repair = Math.min(damage, intendedRepair);
    const damageAfter = Math.max(0, damage - repair);

    const speedRatio = Math.max(0, Math.min(1, speed / maxSpeed));
    const bonusMeters = Math.round(fuel * 2 + dmgClean * 150 + speedRatio * 80);

    return {
        fuelGain: Math.round(fuelGain),
        repair: Math.round(repair),
        bonusMeters: Math.round(bonusMeters),
        fuelAfter,
        damageAfter: Math.round(damageAfter),
    };
};

/** Integer HUD / overlay number — no long floats like 32.148% or 17.6 dmg. */
export const roundHudNumber = (n) => Math.round(Number(n) || 0) || 0;

const applyClearReward = (player, cfg) => {
    const reward = computeLevelClearReward(player, cfg);
    player.fuel = reward.fuelAfter;
    player.damage = reward.damageAfter;
    return reward;
};

const rewardSummary = (reward) => (
    `+${roundHudNumber(reward.fuelGain)} fuel, −${roundHudNumber(reward.repair)} damage, +${roundHudNumber(reward.bonusMeters)} m bonus`
);

/** Finished the campaign (all 5 levels) rather than totaling the car or running dry. */
export const isCampaignWin = (state) => Boolean(
    state?.isFinished && !state?.isCrashed && !state?.isOutOfFuel,
);

const placeFuelPacks = (trackLength, count, rng = Math.random) => {
    const packs = [];
    if (count <= 0) return packs;
    const span = Math.max(200, trackLength - 250);
    for (let i = 0; i < count; i += 1) {
        const frac = (i + 0.5) / count;
        const jitter = (rng() - 0.5) * (span / (count * 2));
        const dist = Math.max(120, Math.min(trackLength - 80, 150 + frac * span + jitter));
        packs.push({ lane: Math.floor(rng() * LANES), distance: dist, kind: 'fuel' });
    }
    return packs;
};

/** Same-lane pack reached this tick. Swept so high speed cannot skip the 12 m window. */
export const packReachedThisTick = (pack, playerLane, distBefore, distAfter, range) => {
    if (Number(pack?.lane) !== Number(playerLane)) return false;
    const prevGap = Number(pack.distance) - Number(distBefore);
    const currGap = Number(pack.distance) - Number(distAfter);
    return prevGap >= 0 && currGap <= range;
};

const pickValidPackLane = (dist, level, trackLength, rng) => {
    const lanes = validLanesForCount(activeLanes(dist, level, trackLength));
    if (!lanes.length) return 1;
    return lanes[Math.floor(rng() * lanes.length) % lanes.length];
};

export const placeHealthPacks = (trackLength, count, fuelPacks = [], rng = Math.random, level = 1) => {
    const packs = [];
    if (count <= 0) return packs;
    const span = Math.max(200, trackLength - 250);
    const fuels = fuelPacks || [];
    const tooClose = (lane, dist, others) => others.some(
        (p) => Number(p.lane) === lane && Math.abs(Number(p.distance) - dist) < PACK_CLEAR,
    );
    for (let i = 0; i < count; i += 1) {
        const frac = (i + 0.22) / count;
        const jitter = (rng() - 0.5) * (span / (count * 2));
        let dist = Math.max(140, Math.min(trackLength - 90, 180 + frac * span + jitter));
        let lane = pickValidPackLane(dist, level, trackLength, rng);
        if (tooClose(lane, dist, fuels) || tooClose(lane, dist, packs)) {
            dist = Math.min(trackLength - 90, dist + PACK_CLEAR);
            const lanes = validLanesForCount(activeLanes(dist, level, trackLength));
            const alt = lanes.find((l) => l !== lane && !tooClose(l, dist, fuels) && !tooClose(l, dist, packs));
            lane = alt != null ? alt : (lanes[0] ?? 1);
        }
        packs.push({ lane, distance: dist, kind: 'health' });
    }
    return packs;
};

export const freshRace = (level = 1, extra = {}) => {
    const cfg = levelCfg(level);
    const carry = extra.playerCarry;
    const fuel = carry != null
        ? Math.min(100, Math.max(0, Number(carry.fuel) || 0))
        : 100;
    const fuelPacks = placeFuelPacks(cfg.track_length, cfg.fuel_pack_count);
    return {
        level,
        player: { lane: 1, distance: 0, speed: cfg.start_speed, damage: 0, fuel },
        obstacles: [],
        fuelPacks,
        healthPacks: placeHealthPacks(cfg.track_length, cfg.health_pack_count, fuelPacks, Math.random, level),
        tick: extra.tick || 0,
        campaignDistance: extra.campaignDistance || 0,
        isFinished: false,
        isCrashed: false,
        isOutOfFuel: false,
        skidding: false,
        levelClear: false,
        lastReward: null,
        message: `Level ${level}/${MAX_LEVEL} · ${roadProfileForLevel(level).name} — collect fuel and health packs, dodge traffic, hit the finish.`,
    };
};

/** Start the next stage with carried fuel; damage always resets to 0. */
export const continueFromLevelClear = (s) => {
    if (!s?.levelClear || s.isFinished || s.level >= MAX_LEVEL) return s;
    return freshRace(s.level + 1, {
        tick: s.tick,
        campaignDistance: s.campaignDistance,
        playerCarry: { fuel: s.player.fuel },
    });
};

/** Pure tick used by the play loop and tests. `rng` defaults to Math.random. */
export const advanceRace = (s, action = 'tick', rng = Math.random) => {
    if (!s || s.isFinished || s.levelClear) return s;
    const cfg = levelCfg(s.level);
    const player = { ...s.player };
    let obstacles = [...s.obstacles];
    let fuelPacks = [...(s.fuelPacks || [])];
    let healthPacks = [...(s.healthPacks || [])];
    let message = s.message;
    let isFinished = false;
    let isCrashed = false;
    let isOutOfFuel = false;
    let skidding = false;
    const level = s.level;
    let campaignDistance = s.campaignDistance || 0;
    let levelClear = false;
    let lastReward = null;

    const trackLen = cfg.track_length;
    const scrapeEdge = (side) => {
        if (isPlayerStopped(player.speed)) {
            message = `Stopped at the ${side}.`;
            return 0;
        }
        const impactSpeed = player.speed;
        const dmg = applySpeedImpact(player, cfg, 'edge');
        skidding = true;
        message = `Skid on the ${side}! +${dmg} dmg at ${roundHudNumber(impactSpeed)} km/h.`;
        return dmg;
    };

    if (action === 'accelerate') {
        player.speed = Math.min(cfg.max_speed, player.speed + cfg.accel_step);
        message = player.speed < cfg.max_speed ? 'Accelerating.' : 'Max speed reached!';
    } else if (action === 'brake') {
        player.speed = Math.max(cfg.min_speed, player.speed - cfg.brake_step);
        message = player.speed > 0 ? 'Reducing speed.' : 'Stopped.';
    } else if (action === 'lane_left') {
        const target = player.lane - 1;
        if (target < 0 || !laneValidAt(target, player.distance, level, trackLen)) {
            scrapeEdge(target < 0 ? 'left shoulder' : 'roadway edge');
        } else {
            player.lane = target;
            message = `Switched to lane ${player.lane + 1}.`;
        }
    } else if (action === 'lane_right') {
        const target = player.lane + 1;
        if (target >= LANES || !laneValidAt(target, player.distance, level, trackLen)) {
            scrapeEdge(target >= LANES ? 'right shoulder' : 'roadway edge');
        } else {
            player.lane = target;
            message = `Switched to lane ${player.lane + 1}.`;
        }
    } else {
        message = 'Cruising...';
    }

    // Drain first, then still move this tick. After pickups/crashes: empty tank
    // short of the finish → out of fuel (episode over). Crossing the line still
    // clears the level even if fuel is 0 after this drain.
    const vmax = Math.max(1, Number(cfg.max_speed) || 1);
    player.fuel = Math.max(0, player.fuel - (player.speed / vmax) * cfg.fuel_drain);
    const distBefore = player.distance;
    player.distance += player.speed * SPEED_DIST_SCALE;

    if (!skidding && !laneValidAt(player.lane, player.distance, level, trackLen)) {
        scrapeEdge('roadway edge');
    }

    if (wantsObstacleSpawn(level, cfg, player.distance, obstacles, rng, trackLen)) {
        const spawned = spawnObstacle(player.distance, level, rng, obstacles);
        if (spawned) obstacles.push(spawned);
    }

    obstacles = obstacles.map((obs) => {
        const rel = obstacleRelSpeed(obs);
        if (!rel) return obs;
        return { ...obs, distance: obs.distance - rel * SPEED_DIST_SCALE };
    });
    obstacles = stepWeavers(obstacles, level, trackLen, rng);

    const collected = fuelPacks.filter((pack) => (
        packReachedThisTick(pack, player.lane, distBefore, player.distance, FUEL_PICKUP_RANGE)
    ));
    if (collected.length) {
        fuelPacks = fuelPacks.filter((p) => !collected.includes(p));
        player.fuel = Math.min(100, player.fuel + cfg.fuel_pack_value * collected.length);
        message = `Fuel pack collected! Tank at ${player.fuel.toFixed(0)}%.`;
    }

    const collision = obstacles.find((obs) => {
        if (Number(obs.lane) !== Number(player.lane)) return false;
        const rel = obstacleRelSpeed(obs);
        const prevGap = (obs.distance + rel * SPEED_DIST_SCALE) - distBefore;
        const currGap = obs.distance - player.distance;
        return prevGap >= 0 && currGap <= COLLISION_RANGE;
    });
    if (collision) {
        obstacles = obstacles.filter((o) => o !== collision);
        const impactSpeed = player.speed;
        if (isPlayerStopped(impactSpeed)) {
            message = 'Traffic passed while stopped.';
        } else {
            const kind = vehicleKindOf(collision);
            const dmg = applySpeedImpact(player, cfg, kind);
            skidding = true;
            message = `Crash vs ${kind} at ${roundHudNumber(impactSpeed)} km/h! +${dmg} dmg.`;
        }
    }

    // Heal after collisions/edge so a same-tick crash cannot hide a successful pickup.
    const healed = healthPacks.filter((pack) => (
        packReachedThisTick(pack, player.lane, distBefore, player.distance, HEALTH_PICKUP_RANGE)
    ));
    if (healed.length) {
        healthPacks = healthPacks.filter((p) => !healed.includes(p));
        player.damage = 0;
        message = `Health pack! Damage ${roundHudNumber(player.damage)}%.`;
    }

    if (player.damage >= 100) {
        player.damage = 100;
        isCrashed = true;
        isFinished = true;
        lastReward = null;
        message = 'Vehicle totaled.';
    }

    if (!isCrashed && player.distance >= cfg.track_length) {
        const reward = applyClearReward(player, cfg);
        campaignDistance += cfg.track_length + reward.bonusMeters;
        lastReward = {
            ...reward,
            completedLevel: level,
            nextLevel: level < MAX_LEVEL ? level + 1 : null,
            nextName: level < MAX_LEVEL ? roadProfileForLevel(level + 1).name : null,
        };
        if (level < MAX_LEVEL) {
            levelClear = true;
            message = `Level ${level} complete! Next: ${lastReward.nextName}. Rewards: ${rewardSummary(reward)}.`;
        } else {
            isFinished = true;
            message = `All 5 levels complete! Rewards: ${rewardSummary(reward)}.`;
        }
    } else if (!isCrashed && player.fuel <= 0) {
        isFinished = true;
        isOutOfFuel = true;
        lastReward = null;
        message = 'Out of fuel.';
    }

    obstacles = obstacles.filter((obs) => obs.distance > player.distance - 20);
    fuelPacks = fuelPacks.filter((pack) => pack.distance > player.distance - 20);
    healthPacks = healthPacks.filter((pack) => pack.distance > player.distance - 20);
    return {
        level,
        player,
        obstacles,
        fuelPacks,
        healthPacks,
        tick: s.tick + 1,
        campaignDistance,
        isFinished,
        isCrashed,
        isOutOfFuel,
        skidding,
        levelClear,
        lastReward,
        message,
    };
};

/** Compact tick snapshot matching GM history + replayAdapters.computeRoadFighterFrame. */
export const roadFighterHistoryEntry = (s, action = 'tick') => {
    const p = s.player || {};
    const dist = Number(p.distance) || 0;
    const nearby = (list) => (list || []).filter((o) => Number(o.distance) > dist - 20).slice(0, 12);
    return {
        tick: s.tick,
        action,
        level: s.level,
        speed: Math.round((Number(p.speed) || 0) * 10) / 10,
        distance: Math.round(dist * 10) / 10,
        campaign_distance: Math.round((Number(s.campaignDistance) || 0) * 10) / 10,
        damage: p.damage,
        fuel: Math.round((Number(p.fuel) || 0) * 10) / 10,
        lane: p.lane,
        player: { lane: p.lane, speed: p.speed, distance: p.distance, fuel: p.fuel, damage: p.damage },
        skidding: Boolean(s.skidding),
        level_clear: Boolean(s.levelClear),
        obstacles: nearby(s.obstacles).map((o) => ({
            lane: o.lane,
            distance: Math.round(Number(o.distance) * 10) / 10,
            kind: o.kind || 'car',
            weave: Boolean(o.weave),
        })),
        fuel_packs: nearby(s.fuelPacks).map((pack) => ({
            lane: pack.lane,
            distance: Math.round(Number(pack.distance) * 10) / 10,
        })),
        health_packs: nearby(s.healthPacks).map((pack) => ({
            lane: pack.lane,
            distance: Math.round(Number(pack.distance) * 10) / 10,
        })),
        score: distanceToScore(Number(s.campaignDistance) || dist),
    };
};

const RoadFighter = () => {
    const play = useManualPlay();
    const [state, setState] = useState(() => freshRace(1));
    const stateRef = useRef(state);
    const phaseRef = useRef(play.phase);
    const pendingRef = useRef(null);
    const inFlightRef = useRef(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createRoadFighterSession,
        deleteSession: deleteRoadFighterSession,
    });

    useEffect(() => { stateRef.current = state; }, [state]);
    useEffect(() => { phaseRef.current = play.phase; }, [play.phase]);

    const applyGm = useCallback((data) => {
        const next = gmToRoadView(data);
        if (next.lastReward) {
            next.lastReward.completedLevel = next.level;
            if (!next.lastReward.nextName && next.level < MAX_LEVEL) {
                next.lastReward.nextName = roadProfileForLevel(next.level + 1).name;
            }
        }
        stateRef.current = next;
        setState(next);
        return next;
    }, []);

    const startGame = useCallback(async () => {
        try {
            gm.setBusy(true);
            const data = await gm.createFreshSession();
            const next = applyGm(data);
            pendingRef.current = null;
            recorder.reset({
                init_state: { player: { ...next.player } },
                track_length: next.trackLength,
                level: next.level,
            });
            play.startGame();
        } catch (err) {
            console.error(err);
            setState((s) => ({ ...s, message: gmStartErrorMessage(err) }));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, gm, play, recorder]);

    const endGame = useCallback(async () => {
        const last = stateRef.current;
        await gm.clearSession();
        play.endGame({ score: distanceToScore(last.campaignDistance || last.player?.distance || 0) });
        const next = freshRace(1);
        stateRef.current = next;
        setState(next);
        pendingRef.current = null;
    }, [gm, play]);

    const continueFromClear = useCallback(async () => {
        if (!stateRef.current.levelClear) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            // Next tick on GM resumes the following stage.
            const data = await moveRoadFighterSession(sid, 'tick');
            const next = applyGm(data);
            recorder.recordTick(roadFighterHistoryEntry(next, 'tick'));
            pendingRef.current = null;
        } catch (err) {
            console.warn('Road Fighter continue failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, gm, recorder]);

    const advanceTick = useCallback(async (action = 'tick') => {
        if (phaseRef.current !== 'playing') return;
        const s = stateRef.current;
        if (s.isFinished || s.levelClear) return;
        if (inFlightRef.current) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        inFlightRef.current = true;
        try {
            const data = await moveRoadFighterSession(sid, action);
            const next = applyGm(data);
            recorder.recordTick(roadFighterHistoryEntry(next, action));
            if (next.isFinished) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: distanceToScore(next.campaignDistance || next.player?.distance || 0),
                    extra: {
                        distance: next.player?.distance,
                        campaign_distance: next.campaignDistance,
                        level: next.level,
                        is_finished: true,
                        is_crashed: next.isCrashed,
                        is_out_of_fuel: next.isOutOfFuel,
                        track_length: next.trackLength,
                    },
                });
                await gm.clearSession();
            }
        } catch (err) {
            console.warn('Road Fighter GM tick failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, gm, play, recorder]);

    useEffect(() => {
        if (play.phase !== 'playing') return undefined;
        const id = setInterval(() => {
            if (phaseRef.current !== 'playing') return;
            if (stateRef.current.isFinished || stateRef.current.levelClear) {
                pendingRef.current = null;
                return;
            }
            const pending = pendingRef.current;
            pendingRef.current = null;
            advanceTick(pending || 'tick');
        }, TICK_MS);
        return () => clearInterval(id);
    }, [play.phase, advanceTick]);

    const onKey = useCallback((e) => {
        if (stateRef.current.levelClear) {
            if (e.key === 'Enter' || e.key === ' ') {
                continueFromClear();
                e.preventDefault();
            }
            return;
        }
        if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            pendingRef.current = 'lane_left';
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            pendingRef.current = 'lane_right';
            e.preventDefault();
        } else if (['ArrowUp', 'w', 'W'].includes(e.key)) {
            pendingRef.current = 'accelerate';
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
            pendingRef.current = 'brake';
            e.preventDefault();
        }
    }, [continueFromClear]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [continueFromClear],
    });

    const player = state.player;
    const cfg = levelCfg(state.level);
    const progress = Math.min(100, Math.max(0, (player.distance / cfg.track_length) * 100));
    const remainingM = Math.max(0, cfg.track_length - player.distance);
    const reward = state.lastReward;
    const fuelPct = roundHudNumber(player.fuel);
    const dmgPct = roundHudNumber(player.damage);
    const campaignWon = isCampaignWin(state);
    const winSetup = campaignWon && reward ? (
        <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
            <Typography variant="subtitle2" sx={{ color: '#ffe066', fontWeight: 800, letterSpacing: 1, mb: 1 }}>
                Game Won
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`+${roundHudNumber(reward.fuelGain)} fuel`} sx={{ bgcolor: 'rgba(78,205,196,0.2)', color: '#4ECDC4' }} />
                <Chip size="small" label={`−${roundHudNumber(reward.repair)} damage`} sx={{ bgcolor: 'rgba(255,183,77,0.2)', color: '#ffb347' }} />
                <Chip size="small" label={`+${roundHudNumber(reward.bonusMeters)} m bonus`} sx={{ bgcolor: 'rgba(255,107,107,0.2)', color: ACCENT }} />
            </Stack>
            <Typography sx={{ color: COLOR.text, textAlign: 'center' }}>
                {rewardSummary(reward)}
            </Typography>
        </Box>
    ) : null;

    const clearBanner = state.levelClear && !state.isFinished && reward ? (
        <Box sx={{ textAlign: 'center', maxWidth: 380 }}>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 0.5 }}>
                Level {reward.completedLevel} complete
            </Typography>
            <Typography sx={{ color: ACCENT, fontWeight: 700, mb: 1.5 }}>
                Next: {reward.nextName}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Chip size="small" label={`+${roundHudNumber(reward.fuelGain)} fuel`} sx={{ bgcolor: 'rgba(78,205,196,0.2)', color: '#4ECDC4' }} />
                <Chip size="small" label={`−${roundHudNumber(reward.repair)} damage`} sx={{ bgcolor: 'rgba(255,183,77,0.2)', color: '#ffb347' }} />
                <Chip size="small" label={`+${roundHudNumber(reward.bonusMeters)} m bonus`} sx={{ bgcolor: 'rgba(255,107,107,0.2)', color: ACCENT }} />
            </Stack>
            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.85rem', mb: 2 }}>
                Tank {fuelPct}% · Damage {dmgPct}% · {roundHudNumber(player.speed)} km/h
            </Typography>
            <Button
                variant="contained"
                size="large"
                onClick={continueFromClear}
                autoFocus
                sx={{
                    bgcolor: ACCENT,
                    color: '#000',
                    fontWeight: 'bold',
                    px: 5,
                    '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.92)' },
                }}
            >
                Continue
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: COLOR.textSubtle, mt: 1.5 }}>
                Enter / Space
            </Typography>
        </Box>
    ) : null;

    return (
        <ArcadeGameShell
            title="Road"
            titleAccent="Fighter"
            subtitle="Five levels — dodge traffic, grab fuel and health packs, and sprint to each finish"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.road_fighter}
            session={session}
            formatScore={(e) => {
                const pts = e.score ?? 0;
                const meters = e.campaign_distance ?? e.distance;
                if (meters != null && meters !== pts) {
                    return `${pts} (${Math.round(Number(meters))} m)`;
                }
                return String(pts);
            }}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
                bannerContent={clearBanner}
                won={campaignWon}
                overTitle={campaignWon ? 'You Won!' : (state.isOutOfFuel ? 'Out of fuel' : undefined)}
                overContent={winSetup}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Lv ${state.level} · ${roadProfileForLevel(state.level).name}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`${player.speed.toFixed(0)} / ${cfg.max_speed} km/h`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Dmg ${roundHudNumber(player.damage)}%`} size="small" color={player.damage >= 75 ? 'error' : 'warning'} />
                        {state.skidding && <Chip label="Skid!" size="small" color="error" />}
                        <Chip
                            label={state.isFinished ? (state.isCrashed ? 'Crashed' : (state.isOutOfFuel ? 'Out of fuel' : 'Finished')) : (state.levelClear ? 'Stage clear' : 'Racing')}
                            size="small"
                            color={state.isFinished ? ((state.isCrashed || state.isOutOfFuel) ? 'error' : 'success') : (state.levelClear ? 'success' : 'warning')}
                        />
                    </Stack>
                    {state.message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{state.message}</Typography>}
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center', gap: 1.5 }}>
                        <TrackProgressMeter value={progress} remainingM={remainingM} color={ACCENT} />
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderRadius: 2, border: '1px solid rgba(255,107,107,0.25)', bgcolor: '#0a1210' }}>
                            <RoadFighterScene
                                lane={player.lane}
                                distance={player.distance}
                                obstacles={state.obstacles}
                                fuelPacks={state.fuelPacks}
                                healthPacks={state.healthPacks}
                                level={state.level}
                                skidding={state.skidding}
                                trackLength={cfg.track_length}
                                width={280}
                                height={360}
                            />
                        </Box>
                        <FuelMeter value={player.fuel} />
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default RoadFighter;
